/**
 * Utility functions for Excel-like table functionality
 * Used across multiple table components for consistent inline editing behavior
 */

/**
 * Parse pasted data and determine if it's tab-delimited (Excel) or plain text
 * Returns array of rows, where each row is an array of cell values
 */
export const parsePasteData = (pasteData: string): string[][] => {
  const trimmed = pasteData.trim();
  const hasTabs = trimmed.includes('\t');

  if (hasTabs) {
    // Tab-delimited data (from Excel/spreadsheet)
    return trimmed.split('\n').filter(r => r.trim()).map(row => row.split('\t'));
  } else {
    // Plain text - could be multiple lines or single value
    const lines = trimmed.split('\n').filter(r => r.trim());

    if (lines.length === 1) {
      // Single value - fill current field only
      return [[lines[0]]];
    } else {
      // Multiple lines without tabs - treat as values for consecutive fields in the same row
      return [lines];
    }
  }
};

/**
 * Calculate the next cell position when Tab is pressed
 * Returns { row, fieldIndex } or null if at table boundary
 */
export const calculateNextCell = <T extends string>(
  currentRow: number,
  currentFieldIndex: number,
  totalRows: number,
  totalFields: number,
  isShiftKey: boolean
): { row: number; fieldIndex: number } | null => {
  if (isShiftKey) {
    // Shift+Tab: Move backwards
    if (currentFieldIndex > 0) {
      // Move to previous field in same row
      return { row: currentRow, fieldIndex: currentFieldIndex - 1 };
    } else if (currentRow > 0) {
      // Move to last field of previous row
      return { row: currentRow - 1, fieldIndex: totalFields - 1 };
    }
    return null; // At beginning of table
  } else {
    // Tab: Move forwards
    if (currentFieldIndex < totalFields - 1) {
      // Move to next field in same row
      return { row: currentRow, fieldIndex: currentFieldIndex + 1 };
    } else if (currentRow < totalRows - 1) {
      // Move to first field of next row
      return { row: currentRow + 1, fieldIndex: 0 };
    }
    return null; // At end of table
  }
};

/**
 * Apply pasted data to rows starting from a specific position
 * Handles foreign key matching by name for relationship fields
 */
export const applyPastedData = <TRow, TRelated extends { id?: number; name: string }>(
  existingRows: TRow[],
  pastedRows: string[][],
  startRowIndex: number,
  startFieldIndex: number,
  fieldOrder: (keyof TRow)[],
  relatedEntitiesMap: Map<keyof TRow, TRelated[]>,
  createEmptyRow: () => TRow
): TRow[] => {
  const updated = [...existingRows];

  pastedRows.forEach((cells, rowOffset) => {
    const targetRowIndex = startRowIndex + rowOffset;

    // Ensure we have enough rows
    while (updated.length <= targetRowIndex) {
      updated.push(createEmptyRow());
    }

    cells.forEach((cell, cellOffset) => {
      const fieldIndex = startFieldIndex + cellOffset;
      if (fieldIndex < fieldOrder.length) {
        const field = fieldOrder[fieldIndex];
        let value: any = cell.trim();

        // Check if this field has related entities (foreign key)
        const relatedEntities = relatedEntitiesMap.get(field);
        if (relatedEntities) {
          // Try to match by name
          const match = relatedEntities.find(e =>
            e.name.toLowerCase() === value.toLowerCase()
          );
          value = match?.id ?? (updated[targetRowIndex][field] as any);
        }

        updated[targetRowIndex] = { ...updated[targetRowIndex], [field]: value };
      }
    });
  });

  return updated;
};

/**
 * Calculate row background color based on state
 */
export const getRowBackgroundColor = (
  isFocused: boolean,
  isNew: boolean,
  isEvenRow: boolean
): string => {
  if (isFocused) {
    return 'rgba(33, 150, 243, 0.12)'; // Blue hue for focused row
  }
  if (isNew) {
    return 'grey.100'; // Light grey for new row
  }
  return isEvenRow ? 'inherit' : 'grey.50'; // Alternating rows
};

/**
 * Calculate row hover background color
 */
export const getRowHoverColor = (isFocused: boolean): string => {
  return isFocused ? 'rgba(33, 150, 243, 0.18)' : 'action.hover';
};
